import { useCallback, useRef, useState } from "react";
import { api } from "../api";

/** Safari ne sait pas produire du WebM : on lui laisse son mp4, ffmpeg lira les deux. */
function pickMimeType(): string | undefined {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

export type MicState = "idle" | "recording" | "sending" | "error";

/**
 * Prendre le mic. On enregistre, puis on envoie : le clip passe à l'antenne
 * quelques secondes plus tard et coupe la musique pour tout le monde.
 */
export function useMic(onDuck: (ducked: boolean) => void) {
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const [state, setState] = useState<MicState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState(0);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const mimeType = pickMimeType();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      rec.onstop = () => stream.getTracks().forEach((t) => t.stop());
      rec.start();
      recorder.current = rec;
      // on s'écoute mal en parlant par-dessus la musique
      onDuck(true);
      setStartedAt(Date.now());
      setState("recording");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Micro inaccessible");
      setState("error");
    }
  }, [onDuck]);

  const stop = useCallback(async () => {
    const rec = recorder.current;
    if (!rec || rec.state === "inactive") return;

    const done = new Promise<Blob>((resolve) => {
      rec.addEventListener(
        "stop",
        () => resolve(new Blob(chunks.current, { type: rec.mimeType || "audio/webm" })),
        { once: true },
      );
    });
    rec.stop();
    setState("sending");

    try {
      const clip = await done;
      if (clip.size < 2_000) throw new Error("Trop court — reste appuyé le temps de parler");
      await api.mic(clip);
      setState("idle");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Envoi raté");
      setState("error");
    } finally {
      recorder.current = null;
      onDuck(false);
    }
  }, [onDuck]);

  return { state, error, start, stop, elapsedMs: state === "recording" ? Date.now() - startedAt : 0 };
}
